// The venue library — the titles a venue offers, drawn from the platform catalogue (F-CAT-02;
// spec/13 §9.1, spec/04 §5).
//
//   GET    /v1/organizations/{o}/venues/{v}/library                the venue's library     library:read
//   POST   /v1/organizations/{o}/venues/{v}/library                add a catalogue game    library:manage
//   PATCH  /v1/organizations/{o}/venues/{v}/library/{entryId}      edit an entry           library:manage
//   DELETE /v1/organizations/{o}/venues/{v}/library/{entryId}      remove an entry         library:manage
//
// A venue library is the venue's own selection **from the platform catalogue** (HQ's `game`, mirrored
// from IGDB — F-CAT-01): a venue adds a catalogue game, chooses the **kiosk category** it appears
// under, the **station classes** it is offered on (empty means every class), whether it is
// **featured**, whether it is **visible** to players, and the order it sorts in. The kiosk shows a
// venue only what its library holds — the catalogue is global, the library is per venue.
//
// **Reads its game from the catalogue.** An entry carries the mirrored `game` (name, cover, genres),
// so the console and the kiosk render the title without a second call. The catalogue game is
// referenced by our own `gam_…` id (never IGDB's, spec/14 §6): the library depends on our record, not
// on the provider being reachable at read time.
//
// **Apps too** (spec/14 §6): the catalogue holds HQ-added apps (browsers, voice chat, launchers…) beside
// the mirrored games, and a venue adds one exactly as it adds a game; `browse` takes a `kind` filter, and
// the console defaults an app's category to the one it suggests. Per-station install state is
// `station-installs.ts` (F-CAT-03).
//
// **Custom apps** (spec/13 §9): an entry offers EITHER a catalogue title (`game`) OR one of the organisation's
// own custom apps (`customApp`, `custom-apps.ts`) — exactly one of the two is set. Adding one is the same
// `POST …/library` with `{ customAppId }` in place of `{ gameId }`. Deferred: the per-venue launch-recipe
// override, in-session detection (F-CAT-04) and the update matrix (F-CAT-06).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { CustomAppIdSchema, GameIdSchema, LibraryEntryIdSchema, OrganizationIdSchema, StationClassIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { LIBRARY_MANAGE, LIBRARY_READ } from "../permissions/index.js";
import { PAGE_DEFAULT_LIMIT, PAGE_MAX_LIMIT, PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { CustomAppSchema } from "./custom-apps.js";
import { CatalogueKindSchema, GameSchema } from "./hq-catalogue.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/**
 * The kiosk categories a library entry is grouped under (spec/13 §9.1). The kiosk's tabs and rows are
 * built from these; a venue puts each title under exactly one. Lower-case codes on the wire; the kiosk
 * and console render the display name. `games` is the default a player expects most titles under.
 */
export const LIBRARY_CATEGORIES = ['games', 'competitive', 'casual', 'apps', 'browsers', 'voice', 'media', 'tools'];
export const LibraryCategorySchema = z.enum(LIBRARY_CATEGORIES).openapi('LibraryCategory', {
    description: 'The kiosk category a library entry is grouped under (spec/13 §9.1): `games`, `competitive`, `casual`, `apps`, ' +
        '`browsers`, `voice`, `media` or `tools`.',
});
/**
 * The station classes an entry is offered on — our own `scl_…` ids, all of the entry's venue. An
 * **empty** list means every class: the behaviour-preserving default, so a title added with no classes
 * chosen shows everywhere rather than nowhere.
 */
const classIdsSchema = z
    .array(StationClassIdSchema)
    .max(64)
    .openapi({ description: 'The station classes this title is offered on. Empty means every class of the venue.' });
/** The order an entry sorts in within its category, low first. Ties break by name. */
const sortOrderSchema = z.int().min(0).max(100_000).openapi({ description: 'The sort order within the category, low first.' });
/** One title a venue offers, drawn from the platform catalogue (spec/04 §5, spec/13 §9.1). */
export const LibraryEntrySchema = z
    .object({
    id: LibraryEntryIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    /**
     * The platform catalogue title this entry offers — mirrored from IGDB (F-CAT-01) or an HQ app, referenced by
     * our own id — or `null` when the entry offers one of the organisation's custom apps (`customApp`).
     */
    game: GameSchema.nullable(),
    /** The organisation's custom app this entry offers (spec/13 §9), or `null` for a catalogue title. Exactly one of `game`/`customApp` is set. */
    customApp: CustomAppSchema.nullable(),
    category: LibraryCategorySchema,
    classIds: classIdsSchema,
    sortOrder: sortOrderSchema,
    /** Featured in the kiosk showcase for this venue. */
    featured: z.boolean(),
    /** Shown to players. A hidden entry stays in the library but the kiosk does not list it. */
    visible: z.boolean(),
    /**
     * The venue turned the catalogue's `requiresElevation` flag off for itself (spec/11 §3.5): its stations launch the
     * title as the player, not through the elevated service. Default `false` — the catalogue's flag applies.
     */
    elevationDisabled: z.boolean(),
    /**
     * **Install for players** (F-CAT-09, ADR-0045): the venue's stations keep the app installed in their player account —
     * a missing one is installed at the next idle moment, by the catalogue app's `provisioning` recipe. Only an app
     * with a recipe can have it on. Default `false`: nothing is installed.
     */
    installForPlayers: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('LibraryEntry', { description: "One title a venue offers, from the platform catalogue (spec/13 §9.1)." });
export const LibraryEntryListSchema = paginated(LibraryEntrySchema, 'LibraryEntryList');
/** The body of `POST …/library`: the catalogue title or custom app to add (exactly one), and how to offer it. */
export const AddLibraryEntrySchema = z
    .object({
    /** The catalogue title (`gam_…`) to add — one already in the platform catalogue. Send this or `customAppId`. */
    gameId: GameIdSchema.optional(),
    /** One of the organisation's custom apps (`app_…`, spec/13 §9) to add. Send this or `gameId`. */
    customAppId: CustomAppIdSchema.optional(),
    category: LibraryCategorySchema,
    classIds: classIdsSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    featured: z.boolean().optional(),
    visible: z.boolean().optional(),
})
    .strict()
    .refine((body) => (body.gameId === undefined) !== (body.customAppId === undefined), {
    message: 'Send exactly one of gameId and customAppId.',
    path: ['gameId'],
})
    .openapi('AddLibraryEntry', { description: 'Add a platform-catalogue title, or one of the organisation’s custom apps, to a venue library.' });
/** The body of `PATCH …/library/{entryId}`: only the fields sent change. The game an entry offers is fixed. */
export const UpdateLibraryEntrySchema = z
    .object({
    category: LibraryCategorySchema,
    classIds: classIdsSchema,
    sortOrder: sortOrderSchema,
    featured: z.boolean(),
    visible: z.boolean(),
    /** Turn the catalogue's `requiresElevation` off for this venue (`true`), or back on (`false`). Audited like any edit. */
    elevationDisabled: z.boolean(),
    /**
     * Turn **Install for players** on or off (F-CAT-09, ADR-0045). On is refused (`400`, on `installForPlayers`) for a
     * title with no provisioning recipe; off is always accepted. Audited like any edit.
     */
    installForPlayers: z.boolean(),
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateLibraryEntry', {
    description: 'Edit a venue library entry — its category, classes, order, featured flag, visibility, the elevation override or Install for players.',
});
const LibraryEntryParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, entryId: LibraryEntryIdSchema })
    .openapi('LibraryEntryParams');
// ---------------------------------------------------------------------------------------------
// Browsing the platform catalogue to add to a venue library (F-CAT-02, spec/13 §9.1)
// ---------------------------------------------------------------------------------------------
//
// "Add by search" (spec/13 §9.1) needs the venue to see the platform catalogue, which HQ curates and
// the venue may not manage. This is a **read of the global `game` table in the venue's scope** — the
// app role holds `SELECT` on `game` (the F-CAT-02 grant) — with each game marked whether it is already
// in this venue's library (and if so, its entry id, so the console can jump to it). It never calls the
// provider: it reads our own mirrored rows.
/** One platform-catalogue game as the venue library's add screen sees it: the game, and whether it is already offered here. */
export const VenueCatalogueGameSchema = z
    .object({
    /** The platform-catalogue game — HQ's mirror of an IGDB base game (F-CAT-01). */
    game: GameSchema,
    /** Is this game already in this venue's library? */
    inLibrary: z.boolean(),
    /** The venue library entry that offers it, when `inLibrary`; `null` otherwise. */
    entryId: LibraryEntryIdSchema.nullable(),
})
    .openapi('VenueCatalogueGame', { description: 'A platform-catalogue game and whether a venue already offers it (spec/13 §9.1).' });
export const VenueCatalogueGameListSchema = paginated(VenueCatalogueGameSchema, 'VenueCatalogueGameList');
/** The query of `GET …/catalogue`: cursor pagination and an optional name search. */
export const BrowseVenueCatalogueQuerySchema = z
    .object({
    cursor: z.string().min(1).optional().openapi({ description: 'The `page.cursor` from the previous response.' }),
    limit: z.coerce.number().int().min(1).max(PAGE_MAX_LIMIT).default(PAGE_DEFAULT_LIMIT).openapi({ description: `Items per page, 1–${PAGE_MAX_LIMIT}.` }),
    q: z.string().trim().min(1).max(120).optional().openapi({ description: 'Filter catalogue games by name (case-insensitive substring).', example: 'counter' }),
    kind: CatalogueKindSchema.optional().openapi({ description: 'Only titles of this kind — `game` or `app`. Absent: both.' }),
})
    .openapi('BrowseVenueCatalogueQuery');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
// A game not in the catalogue, or a class not of this venue, is a 400 with a field error on `gameId` /
// `classIds` — the same shape a station's bad `classId`/`zoneId` uses (spec/13 §9.1).
const badReference = problemResponse('The game is not in the catalogue, the custom app is not the organisation’s (or is archived), a class is not one of this venue’s, or Install for players was turned on for a title with no provisioning recipe.', 'validation-failed');
const alreadyInLibrary = problemResponse('That title is already in this venue’s library.', 'conflict');
export const browseVenueCatalogueRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/catalogue',
    operationId: 'browseVenueCatalogue',
    tags: ['library'],
    summary: 'Browse the platform catalogue to add to a venue library',
    description: 'The platform catalogue (HQ’s mirrored games and added apps, F-CAT-01), newest first, each marked whether this ' +
        'venue already offers it — what the library’s "add by search" screen picks from (spec/13 §9.1). Optional `q` ' +
        'filters by name and `kind` to games or apps. ' +
        'Reads our own `game` table in the venue’s scope; never calls the provider. Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: VenueScopeParamsSchema, query: BrowseVenueCatalogueQuerySchema },
    responses: {
        200: jsonResponse(VenueCatalogueGameListSchema, 'A page of catalogue games, each flagged whether the venue offers it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listLibraryEntriesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library',
    operationId: 'listLibraryEntries',
    tags: ['library'],
    summary: "List a venue's library",
    description: 'The titles the venue offers, oldest first, each with its catalogue game. Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(LibraryEntryListSchema, 'A page of library entries.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const addLibraryEntryRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library',
    operationId: 'addLibraryEntry',
    tags: ['library'],
    summary: 'Add a catalogue game to a venue library',
    description: 'Adds a platform-catalogue title (`gameId`, `gam_…`) or one of the organisation’s custom apps (`customAppId`, ' +
        '`app_…`) to the venue library with its kiosk category, station classes (empty for every class), featured flag ' +
        'and visibility. A game not in the catalogue, a custom app not the organisation’s or archived, or a class not ' +
        'one of this venue’s, is `400`. The same title twice is `409`. Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: AddLibraryEntrySchema } }, required: true },
    },
    responses: {
        201: jsonResponse(LibraryEntrySchema, 'The entry that was added.'),
        400: badReference,
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: alreadyInLibrary,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateLibraryEntryRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/{entryId}',
    operationId: 'updateLibraryEntry',
    tags: ['library'],
    summary: 'Edit a venue library entry',
    description: 'Only the fields sent change — the category, classes, order, featured flag, visibility, the elevation override or ' +
        '**Install for players** (`installForPlayers`, F-CAT-09: the venue’s stations install the app into their player ' +
        'account; turning it on for a title with no provisioning recipe is `400`). The game the entry ' +
        'offers is fixed (remove and re-add to change it). A class not one of this venue’s is `400`. Requires ' +
        '`library:manage`. Audited with the before and after.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: {
        params: LibraryEntryParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateLibraryEntrySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(LibraryEntrySchema, 'The entry as it is now.'),
        400: badReference,
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such entry in this venue’s library.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const removeLibraryEntryRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/library/{entryId}',
    operationId: 'removeLibraryEntry',
    tags: ['library'],
    summary: 'Remove a title from a venue library',
    description: 'Removes the entry from the venue library. The catalogue game itself is untouched — only this venue stops ' +
        'offering it. Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: LibraryEntryParamsSchema },
    responses: {
        204: { description: 'The entry was removed.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such entry in this venue’s library.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const LIBRARY_ROUTES = [
    browseVenueCatalogueRoute,
    listLibraryEntriesRoute,
    addLibraryEntryRoute,
    updateLibraryEntryRoute,
    removeLibraryEntryRoute,
];
//# sourceMappingURL=library.js.map